import { useEffect, useMemo, useRef, useState } from 'react'
import { Trans } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ColumnAdapter,
  ColumnConditionValue,
  ConditionNumericOperator,
  ConditionTextOperator,
  DATE_OPERATOR_WITH_RANGE_RHS,
  DATE_OPERATOR_WITH_RHS,
  DefaultVariableResult,
  NUMERIC_OPERATOR_WITH_INPUT_RHS,
  NUMERIC_OPERATOR_WITH_RANGE_RHS,
  OPERATOR_WITH_BETWEEN_RHS,
  OPERATOR_WITH_CASE_SENSITIVE,
  OPERATOR_WITH_INPUT_RHS,
  TableAdapter,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { ChevronDown } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Switch, SwitchSize } from '@makyoapp/makyo-ui'

import { getOperatorDefaultOption } from '../../shared/condition'
import { VariableSelectV2 } from '../VariableSelectV2/VariableSelectV2'
import { ColumnConditionOperatorSelect } from './ColumnConditionOperatorSelect'
import { ColumnConditionSelect } from './ColumnConditionSelect'

const DEFAULT_FLAGS =
  VariableSelectFeatureFlag.SingleCell |
  VariableSelectFeatureFlag.CurrentRow |
  VariableSelectFeatureFlag.CurrentListItem |
  VariableSelectFeatureFlag.CustomText |
  VariableSelectFeatureFlag.ProfileData |
  VariableSelectFeatureFlag.SessionID |
  VariableSelectFeatureFlag.PageTitle

export interface ConditionItemProps {
  id?: string
  index: number
  colOptions: ColumnAdapter[]
  tables: TableAdapter[]
  table: TableAdapter
  value: ColumnConditionValue
  onChange: (nextValue: ColumnConditionValue) => void
  flags?: VariableSelectFeatureFlag
}

function ConditionItem({
  id,
  index,
  colOptions,
  tables,
  table,
  value,
  onChange,
  flags = DEFAULT_FLAGS,
}: ConditionItemProps) {
  const cond = value.conditions[index]
  const totalConditions = value.conditions.length
  const [showLogicDropdown, setShowLogicDropdown] = useState(false)
  const logicDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (logicDropdownRef.current && !logicDropdownRef.current.contains(event.target as Node)) {
        setShowLogicDropdown(false)
      }
    }

    if (showLogicDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLogicDropdown])

  const colObj = useMemo(() => {
    return colOptions.find((c) => c.id === cond.lhs)
  }, [colOptions, cond.lhs])

  const varColTypes = useMemo(() => {
    if (colObj) {
      if (colObj.type === ColumnTypes.RowID || colObj.type === ColumnTypes.SessionID) {
        return []
      }
      if (colObj.type === ColumnTypes.Lookup) {
        return [ColumnTypes.Lookup, colObj.resolvedType || ColumnTypes.Text]
      }

      if (
        colObj.type === ColumnTypes.Text ||
        [ConditionTextOperator.IsNotContainedIn, ConditionTextOperator.IsContainedIn].includes(
          cond.operator as ConditionTextOperator
        )
      ) {
        return [ColumnTypes.Text, ColumnTypes.Array]
      } else {
        return [colObj.type]
      }
    } else {
      return []
    }
  }, [colObj, cond])

  const handleRemoveCondition = () => {
    const nextCond = [...value.conditions]
    nextCond.splice(index, 1)
    onChange?.({
      isAny: value?.isAny,
      conditions: nextCond,
    })
  }

  const variableFlags = [...DATE_OPERATOR_WITH_RANGE_RHS, ...DATE_OPERATOR_WITH_RHS].includes(cond.operator)
    ? flags | VariableSelectFeatureFlag.DateTime
    : flags

  return (
    <>
      {/* AND/OR Button - Show between conditions */}
      {index > 0 && (
        <div className='flex justify-center w-full relative' ref={logicDropdownRef}>
          <button
            onClick={() => setShowLogicDropdown(!showLogicDropdown)}
            className='flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold text-sm rounded-md transition-colors'
            aria-label='Change logic operator'
            aria-expanded={showLogicDropdown}
          >
            <span>{value.isAny ? 'OR' : 'AND'}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showLogicDropdown ? 'rotate-180' : ''}`} />
          </button>

          {showLogicDropdown && (
            <div className='absolute top-full left-1/2 transform -translate-x-1/2 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-10 min-w-[120px]'>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: false,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  !value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>AND</Trans>
              </button>
              <button
                onClick={() => {
                  onChange?.({
                    ...value,
                    isAny: true,
                  })
                  setShowLogicDropdown(false)
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  value?.isAny ? 'text-teal-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <Trans>OR</Trans>
              </button>
            </div>
          )}
        </div>
      )}

      <div className='flex items-center gap-2 w-full flex-col bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700'>
        <div className='flex flex-col gap-2 w-full'>
          <div className='flex-c w-full'>
            <div className='w-full min-w-0'>
              <ColumnConditionSelect
                id={id ? `${id}-lhs` : undefined}
                value={cond?.lhs}
                onChange={(lhs) => {
                  const nextCond = [...value.conditions]
                  const isSame = nextCond[index].lhs === lhs
                  const newColObj = colOptions.find((c) => c.id === lhs)
                  const nextOp = isSame
                    ? nextCond[index].operator
                    : newColObj
                      ? getOperatorDefaultOption(newColObj, table, tables)
                      : ConditionTextOperator.IsNotEmpty

                  let extra: Record<string, unknown> = {}
                  if (!isSame) {
                    extra = {
                      rhs: {
                        flag: VariableSelectFeatureFlag.CustomText,
                        value:
                          NUMERIC_OPERATOR_WITH_INPUT_RHS.includes(nextOp as ConditionNumericOperator) ||
                          NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(nextOp as ConditionNumericOperator)
                            ? '0'
                            : '',
                      },
                    }

                    if (NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(nextOp as ConditionNumericOperator)) {
                      extra.rhsEnd = {
                        flag: VariableSelectFeatureFlag.CustomText,
                        value: '0',
                      }
                    }
                  }

                  if (!isSame) {
                    if (DATE_OPERATOR_WITH_RHS.includes(nextOp)) {
                      extra = {
                        rhs: {
                          flag: VariableSelectFeatureFlag.CustomText,
                          value: new Date().toISOString(),
                        },
                      }
                    }
                    if (DATE_OPERATOR_WITH_RANGE_RHS.includes(nextOp)) {
                      extra = {
                        rhs: {
                          flag: VariableSelectFeatureFlag.CustomText,
                          value: new Date().toISOString(),
                        },
                        rhsEnd: {
                          flag: VariableSelectFeatureFlag.CustomText,
                          value: new Date().toISOString(),
                        },
                      }
                    }
                  }
                  nextCond[index] = {
                    ...nextCond[index],
                    lhs,
                    operator: nextOp,
                    ...extra,
                  }
                  onChange({
                    isAny: value?.isAny ?? true,
                    conditions: nextCond,
                  })
                }}
                colOptions={colOptions}
              />
            </div>
            <button
              onClick={handleRemoveCondition}
              className='cursor-pointer flex items-center justify-center self-center hover:bg-gray-100 dark:hover:bg-gray-700 rounded p-2 transition-colors border border-gray-700 w-10 h-10 shrink-0'
              aria-label='Remove condition'
            >
              <MaterialIcon icon='delete' size={18} />
            </button>
          </div>
          <ColumnConditionOperatorSelect
            id={id ? `${id}-op` : undefined}
            selected={cond.operator}
            value={cond}
            colOptions={colOptions}
            table={table}
            onChange={(opt) => {
              const nextCond = [...value.conditions]
              const currentCondition = nextCond[index]
              const currentOp = currentCondition.operator

              // Check if switching between range and single value operators
              const isCurrentBetween =
                DATE_OPERATOR_WITH_RANGE_RHS.includes(currentOp) ||
                NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(currentOp as ConditionNumericOperator)
              const isNewBetween =
                DATE_OPERATOR_WITH_RANGE_RHS.includes(opt) ||
                NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(opt as ConditionNumericOperator)

              // Reset values when switching between single/range operators
              if (isCurrentBetween !== isNewBetween) {
                if (isNewBetween) {
                  // Switching to range: use current value as start
                  nextCond[index] = {
                    ...currentCondition,
                    operator: opt,
                    rhs: currentCondition.rhs,
                    rhsEnd: currentCondition.rhs ? { ...(currentCondition.rhs as DefaultVariableResult) } : undefined,
                  }
                } else {
                  // Switching to single: use start value
                  nextCond[index] = {
                    ...currentCondition,
                    operator: opt,
                    rhs: currentCondition.rhs,
                    rhsEnd: undefined, // Clear end value
                  }
                }
              } else {
                // Same type of operator, just update the operator
                nextCond[index] = {
                  ...currentCondition,
                  operator: opt,
                }
              }

              onChange({
                isAny: value?.isAny ?? true,
                conditions: nextCond,
              })
            }}
          />
        </div>
        {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
          <div className='uppercase text-m-ss'>
            <Trans>Lower Bound</Trans>
          </div>
        )}
        {(OPERATOR_WITH_INPUT_RHS.includes(cond.operator) ||
          DATE_OPERATOR_WITH_RHS.includes(cond.operator) ||
          NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
          DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
          <VariableSelectV2
            id={`${id}-rhs-var`}
            value={cond.rhs}
            onChange={(val) => {
              const nextCond = [...value.conditions]
              nextCond[index] = {
                ...nextCond[index],
                rhs: val as DefaultVariableResult,
              }
              onChange({
                isAny: value?.isAny ?? true,
                conditions: nextCond,
              })
            }}
            featureFlags={variableFlags}
            variableCustomTextOptions={{
              inputType: colObj
                ? colObj.type === ColumnTypes.Number
                  ? 'number'
                  : colObj.type === ColumnTypes.DateTime
                    ? 'datetime'
                    : 'text'
                : 'text',
            }}
            variableColumnOptions={{
              columnTypes: varColTypes,
              excludeColIds: colObj?.id ? [colObj.id] : [], // * dont allow the same column to be selected twice, since it will create a circular reference
            }}
            tableId={table.id}
          />
        )}
        {OPERATOR_WITH_BETWEEN_RHS.includes(cond.operator) && (
          <div className='uppercase text-m-ss'>
            <Trans>Upper Bound</Trans>
          </div>
        )}
        {(NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator) ||
          DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator)) && (
          <VariableSelectV2
            id={`${id}-rhs-var-end`}
            value={cond.rhsEnd}
            onChange={(val) => {
              const nextCond = [...value.conditions]
              nextCond[index] = {
                ...nextCond[index],
                rhsEnd: val as DefaultVariableResult,
              }
              onChange({
                isAny: value?.isAny ?? true,
                conditions: nextCond,
              })
            }}
            featureFlags={
              DATE_OPERATOR_WITH_RANGE_RHS.includes(cond.operator) ? flags | VariableSelectFeatureFlag.DateTime : flags
            }
            variableCustomTextOptions={{
              inputType: NUMERIC_OPERATOR_WITH_RANGE_RHS.includes(cond.operator as ConditionNumericOperator)
                ? 'number'
                : 'datetime',
            }}
            variableColumnOptions={{
              columnTypes: colObj ? [colObj.type] : [],
              excludeColIds: colObj?.id ? [colObj.id] : [],
            }}
          />
        )}
        <div className='flex-c w-full'>
          {OPERATOR_WITH_CASE_SENSITIVE.includes(cond.operator) && (
            <label className='flex-c'>
              <div>
                <Switch
                  size={SwitchSize.XS}
                  checked={cond.caseSensitive}
                  onClick={(isChecked) => {
                    const nextCond = [...value.conditions]
                    nextCond[index] = {
                      ...nextCond[index],
                      caseSensitive: isChecked,
                    }
                    onChange({
                      isAny: value?.isAny ?? true,
                      conditions: nextCond,
                    })
                  }}
                ></Switch>
              </div>
              <div className='text-m-sm whitespace-nowrap'>
                <Trans>Case Sensitive</Trans>
              </div>
            </label>
          )}
        </div>
      </div>
    </>
  )
}

export interface ColumnConditionRepeaterV3Props {
  id?: string
  onChange: (nextValue: ColumnConditionValue) => void
  value: ColumnConditionValue
  colOptions: ColumnAdapter[]
  tables: TableAdapter[]
  table: TableAdapter
  noUserSpecific?: boolean
}

export function ColumnConditionRepeaterV3({
  id,
  value,
  onChange,
  colOptions,
  tables,
  table,
  noUserSpecific,
}: ColumnConditionRepeaterV3Props) {
  const [showLogicDropdown, setShowLogicDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLogicDropdown(false)
      }
    }

    if (showLogicDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLogicDropdown])

  const hasConditions = value && value.conditions && value.conditions.length > 0

  const handleAddCondition = () => {
    onChange({
      isAny: value?.isAny ?? false,
      conditions: [
        ...(value?.conditions ?? []),
        {
          lhs: colOptions[0]?.id,
          operator: getOperatorDefaultOption(colOptions[0], table, tables),
          caseSensitive: false,
          userSpecific: false,
          rhs: undefined,
        },
      ],
    })
  }

  return (
    <div id={id} className='flex flex-col gap-4 w-full'>
      {/* Description Text */}
      {hasConditions ? (
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          {value.isAny ? (
            <Trans>
              Filter rows that match <strong>any</strong> of the conditions below. Add more conditions to refine your
              filter.
            </Trans>
          ) : (
            <Trans>
              Filter rows that match <strong>all</strong> of the conditions below. Add more conditions to refine your
              filter.
            </Trans>
          )}
        </p>
      ) : (
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          <Trans>
            Add conditions below to filter your data. Combine multiple conditions to create precise filters.
          </Trans>
        </p>
      )}

      {/* Condition Items */}
      {hasConditions && (
        <div className='flex gap-3 flex-col w-full'>
          {value.conditions.map((_, i) => {
            return (
              <ConditionItem
                id={id}
                index={i}
                key={i}
                colOptions={colOptions}
                tables={tables}
                table={table}
                value={value}
                onChange={onChange}
              />
            )
          })}
        </div>
      )}

      {/* Add Condition Button */}
      {hasConditions ? (
        <div className='flex justify-start'>
          <Button
            onClick={handleAddCondition}
            variant={ButtonVariant.Ghost}
            className='cursor-pointer flex items-center !text-primary-500 hover:text-primary-600'
          >
            <MaterialIcon size={18} icon='add' />
            <Trans>Add condition</Trans>
          </Button>
        </div>
      ) : (
        <button
          onClick={handleAddCondition}
          className='text-teal-500 hover:text-teal-600 dark:text-teal-400 dark:hover:text-teal-300 font-medium text-sm transition-colors flex items-center gap-2'
        >
          <MaterialIcon icon='add' size={16} />
          <Trans>Add condition</Trans>
        </button>
      )}
    </div>
  )
}
